import { Injectable } from '@angular/core'
import { Observable, Subject, map, tap, take } from 'rxjs'

import { DELETE_CLIENT, GET_CLIENT, UPDATE_CLIENT, UPDATE_API_TOKEN } from '@app/query/client.query'
import { MessageService, ConfirmationService } from 'primeng/api'
import { TranslateService } from '@ngx-translate/core'
import { ServicesSubscriberEvent } from '@app/models/others.model'
import { Client, KEYERA_WEB_SESSION_DURATIONS, SESSION_DURATIONS, UpdateClientInput } from '@app/models/client.model'
import { pick } from 'lodash-es'
import { serializeAs } from '@app/utils/serialize'
import { removeTypename } from '@app/utils/graphql'
import { FormGroup } from '@angular/forms'
import { TranslateMessagesService } from '@app/shared/services/translate-messages.service'
import { ApiService } from '@app/core/graphql/services/api.service'

@Injectable({
  providedIn: 'root',
})
export class DetailsService {
  subscriber: Subject<ServicesSubscriberEvent> = new Subject<ServicesSubscriberEvent>()
  id: string = 'CLIENT'
  sysMessage: any
  constructor(
    private API: ApiService,
    private messageService: MessageService,
    private translate: TranslateService,
    private confirmationService: ConfirmationService,
    private translateMessagesService: TranslateMessagesService,
  ) {
    this.sysMessage = this.translateMessagesService.create(this.id)
  }
  getEmployeesOptions() {
    return new Observable((observer) => {
      observer.next([
        {
          name: '1-10',
          value: '1-10',
        },
        {
          name: '11-25',
          value: '11-25',
        },
        {
          name: '26-100',
          value: '26-100',
        },
        {
          name: '100+',
          value: '100+',
        },
      ])
    })
  }
  getSessionDurations() {
    const minutes = SESSION_DURATIONS.map((el) => el[0])
    return new Observable((observer) => {
      const options: any = []

      for (let i = 0; i < minutes.length; i++) {
        const min = minutes[i]
        const getTransName = () => {
          const numericValue = Number(min)
          if (numericValue < 60) {
            return 'MINUTES'
          } else if (numericValue === 60) {
            return 'HOUR'
          } else {
            return 'HOURS'
          }
        }
        this.translate.get('SHARED.' + getTransName()).subscribe((value) => {
          const numericValue = Number(min)
          options.push({
            name: `${numericValue < 60 ? numericValue : numericValue / 60} ${value}`,
            value: SESSION_DURATIONS[i][1],
          })
          if (i == minutes.length - 1) {
            observer.next(options)
            observer.complete()
          }
        })
      }
    })
  }
  /** Keyera Web Link (YB-2007): lifetime options for a linked browser session ('1h'…'30d'). */
  getKeyeraWebSessionDurations() {
    return new Observable((observer) => {
      const options = KEYERA_WEB_SESSION_DURATIONS.map((duration) => {
        const amount = parseInt(duration)
        const unit = duration.endsWith('h') ? (amount === 1 ? 'HOUR' : 'HOURS') : amount === 1 ? 'DAY' : 'DAYS'
        return {
          name: `${amount} ${this.translate.instant('SHARED.' + unit)}`,
          value: duration,
        }
      })
      observer.next(options)
      observer.complete()
    })
  }

  setDefaultDataToForm(form: FormGroup, updateAs: any = {}) {
    return new Promise((done: any) => {
      this.getCurrentClient()
        .pipe(take(1))
        .subscribe((client: any) => {
          let keys = {
            event_default_password: 'password',
            default_station_type: 'station_type',
            default_session_duration: 'session_duration',
          } as any
          keys = Object.assign({}, keys, updateAs)
          for (const key in keys) {
            const assignKey = keys[key]
            if (key in client && Object.hasOwn(form.controls, assignKey)) {
              const value = client[key]
              form.get(assignKey)!.setValue(value)
            }
          }
          done()
        })
    })
  }
  updateApiToken() {
    //UPDATE_API_TOKEN
    return this.API.privateMutation<any>(UPDATE_API_TOKEN)
  }

  update(payload: Partial<Client>, silence?: boolean) {
    return this.API.privateMutation<any>(UPDATE_CLIENT, (data: any) => {
      const cleanPayload = removeTypename(Object.assign(payload, data))
      return {
        data: serializeAs(UpdateClientInput, pick(cleanPayload, Object.keys(new UpdateClientInput()))) as UpdateClientInput,
      }
    }).pipe(
      tap((data: any) => {
        if (silence) return data
        if (data.success) {
          this.messageService.add({
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
            ...this.sysMessage.message('UPDATE_SUCCESS', data.data),
          })
          this.subscriber.next({
            type: `UPDATE_${this.id}`,
            data: data.data as Client,
          })
        }
      }),
    )
  }

  getCurrentClient(): Observable<Client> {
    return this.API.privateQuery<any>(GET_CLIENT(null)).pipe(
      map((response: any) => {
        if (response.success) {
          this.subscriber.next({
            type: `GET_${this.id}`,
            data: response.data as Client,
          })
          return response.data as Client
        } else {
          return {} as Client
        }
      }),
    )
  }

  getCurrentClientBy(requestFields: string): Observable<Client> {
    return this.API.privateQuery<any>(GET_CLIENT(requestFields)).pipe(
      map((response: any) => {
        if (response.success) {
          this.subscriber.next({
            type: `GET_${this.id}`,
            data: response.data as Client,
          })
          return response.data as Client
        } else {
          return {} as Client
        }
      }),
    )
  }

  safeDeleteCurrentClient(client: Client, silence?: boolean) {
    return new Observable<any>((observer) => {
      this.confirmationService.confirm({
        ...this.sysMessage.confirm('CONFIRM_DELETE', client),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translate.instant('COMMON.BUTTONS.DELETE'),
        rejectLabel: this.translate.instant('COMMON.BUTTONS.CANCEL'),
        defaultFocus: 'reject',
        acceptButtonStyleClass: 'p-button-text',
        accept: () => {
          this.deleteCurrentClient(silence)
            .pipe(take(1))
            .subscribe((data: any) => {
              observer.next(data)
              observer.complete()
            })
        },
        reject: () => {
          observer.next({
            success: false,
          })
          observer.complete()
        },
      })
    })
  }
  deleteCurrentClient(silence?: boolean) {
    return this.API.privateMutation<any>(DELETE_CLIENT).pipe(
      tap((data: any) => {
        if (silence) return data
        if (data.success) {
          this.messageService.add({
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
            ...this.sysMessage.message('DELETE_SUCCESS', data.data),
          })
          this.subscriber.next({
            type: `DELETE_${this.id}`,
            data: data.data as Pick<Client, 'id' | 'name'>,
          })
        }
      }),
    )
  }
}
